/** @jsxRuntime automatic */
/** @jsxImportSource react */
/**
 * What the Fleet health views and the Trends sections share (docs/CONTRACT-fleet-governance.md section 12): the page frame
 * a view draws its header with, a plain page for the gate's states, in-place links, chips from lib/fleet.ts's maps, the
 * facts list, the Export NDJSON link, the note for sources that could not be read and a failed load in the service's words.
 * Kit only (src/ui); no Pulse module.
 */
import { Fragment, type MouseEvent, type ReactNode } from 'react';
import { Download } from 'lucide-react';
import { ApiError } from '../../lib/api';
import type { FleetChip } from '../../lib/fleet';
import { linkClick } from '../../shell/router';
import { Callout, ErrorState, LinkButton, PageHeader, StatusChip, type PageTab } from '../../ui';

/** What the page hands each view: its tabs, the actions every view carries (Settings) and the way to open a PC's drawer. */
export interface FleetFrame {
  tabs: PageTab[];
  activeTab: string;
  actions: ReactNode;
  openPc: (deviceId: string) => void;
}

/** A page with nothing but a message under the shell's header (the gate's checking and hidden states). */
export function PlainPage({ children }: { children: ReactNode }) {
  return (
    <>
      <PageHeader />
      <div className="ui-page">{children}</div>
    </>
  );
}

/** A link to another place in the console, navigating in place; Ctrl-click still opens a tab. */
export function PageLink({ href, children, className, title }: { href: string; children: ReactNode; className?: string; title?: string }) {
  return <a href={href} className={className} title={title} onClick={(event: MouseEvent<HTMLAnchorElement>) => linkClick(event)}>{children}</a>;
}

/** A chip from one of the closed maps (a status, a problem kind, a version, a local connection's state). */
export function Chip({ spec }: { spec: FleetChip }) {
  return <StatusChip tone={spec.tone} plain={spec.plain} title={spec.title}>{spec.label}</StatusChip>;
}

/** A definition list of facts: a drawer's blocks. */
export function Facts({ rows, label }: { rows: Array<[ReactNode, ReactNode]>; label?: string }) {
  return (
    <dl className="ui-facts" aria-label={label}>
      {rows.map(([term, detail], index) => (
        <Fragment key={index}>
          <dt>{term}</dt>
          <dd>{detail}</dd>
        </Fragment>
      ))}
    </dl>
  );
}

/** Export NDJSON: a download of the list or section as filtered (the audited export route). */
export function ExportLink({ href, title = 'Download this list as NDJSON, filtered as shown. The export is itself audited.' }: { href: string; title?: string }) {
  return <LinkButton size="sm" icon={Download} href={href} download title={title}>Export NDJSON</LinkButton>;
}

/** The sources a read could not reach, said once above what they left blank. */
export function SourcesNote({ text }: { text: string | null }) {
  if (!text) return null;
  return <Callout tone="warn">{text}</Callout>;
}

/** A failed load in words (`words` turns the error into a sentence), with its status and Try again. */
export function LoadError({ title, error, words, onRetry, retrying }: { title: ReactNode; error: unknown; words: (error: unknown) => string; onRetry?: () => void; retrying?: boolean }) {
  const status = (error as { status?: unknown } | null)?.status;
  const said = new ApiError(words(error), typeof status === 'number' ? status : 0);
  return <ErrorState compact title={title} error={said} onRetry={onRetry} retrying={retrying} />;
}
